import { Editor } from '@ckeditor/ckeditor5-core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Element } from '@ckeditor/ckeditor5-engine';
// eslint-disable-next-line import/no-extraneous-dependencies
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
// eslint-disable-next-line import/no-extraneous-dependencies
import Writer from '@ckeditor/ckeditor5-engine/src/model/writer';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { v4 as uuidv4 } from 'uuid';
import { ElementDefinition, TagAttributesDefinition } from './Definition';
import Schema from './Schema';

type AttributeDialogProps = {
  editor: Editor;
  node: Node;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

interface Attribute {
  on: boolean;
  name: string;
  value: string;
  parentName: string;
}

interface Tag {
  name: string;
  definition: ElementDefinition;
  attributes: Attribute[];
}

function AttributeDialog({ editor, node, isOpen, setIsOpen }: AttributeDialogProps) {
  /**
   * tagAttributeとは、以下のような構造になっているmodel要素に付加されているview上の要素に対応した属性を格納しているもの。
   * attributesやclassesは必ずあるとは限らない。
   * [
   *  'htmlDivAttributes',
   *  {
   *   attributes: {},
   *   classes: [],
   *  }
   * ]
   */

  // 要素の取り得る属性定義を取得
  const elementDefinition = useMemo(() => {
    if (!node) {
      return undefined;
    }

    if (node.is('$text')) {
      return undefined;
    }

    const element = node as Element;

    let option = '';
    const parent = element?.parent as Element;
    if (/^l\dText$/.test(parent?.name)) {
      option = '@section_id';
    } else {
      const nameForValidation = Schema.getNameForValidation(node);
      if (nameForValidation.startsWith('div')) {
        switch (nameForValidation) {
          case 'div.contents':
            option = '.contents';
            break;

          case 'div.title':
            option = '@ps';
            break;

          default: {
            // ps_no属性がある場合は@psを指定
            const htmlDivAttributes = element.getAttribute('htmlDivAttributes') as unknown as any;
            if (htmlDivAttributes?.attributes?.ps_no) {
              option = '@ps';
            }
            break;
          }
        }
      }
    }

    return Schema.getElementDefinition(element.name, option);
  }, [node]);

  const tagAttributesDefinitions = useMemo(
    () =>
      elementDefinition?.editableAttributes?.reduce((acc, current) => {
        const tagAttributesDefinition = Schema.getTagAttributesDefinition(
          current.name,
          // なぜか型付きなのにエラーになるので以下を指定
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          elementDefinition?.optionForAttributes
        );
        if (tagAttributesDefinition) {
          if (current.onlyExists) {
            const attributes = node.getAttribute(current.name) as unknown;
            if (attributes) {
              acc.push(tagAttributesDefinition);
            }
          } else {
            acc.push(tagAttributesDefinition);
          }
        }

        return acc;
      }, [] as TagAttributesDefinition[]),
    [elementDefinition, node]
  );

  /**
   * 要素のhtml??Attributes属性内から指定された属性値を取得する。
   * @param element 対象要素。
   * @param name 属性名。
   * @param attributesName 対象html??Attributes名（例：htmlDivAttributes）
   * @returns 属性値。存在しなくても空文字が返される。
   */
  const getAttributeInAttributes = (element: Element, name: string, attributesName: string): string => {
    let attributeValue = '' as string | number | boolean | string[] | undefined;
    // html??Attributesという名前の属性値を取得
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attributesValues = element.getAttribute(attributesName) as any;
    if (attributesValues) {
      if ((attributesValues as Record<string, string | number | boolean>).attributes) {
        attributeValue = attributesValues.attributes[name];
      }
    }
    if (!attributeValue) {
      attributeValue = '';
    }

    return attributeValue as string;
  };

  const [tags, setTags] = useState<Tag[]>([]);

  /**
   * 編集用属性値をセットする。
   */
  useEffect(() => {
    if (!node) {
      return;
    }

    if (node.is('$text')) {
      return;
    }

    const element = node as Element;

    setTags(
      tagAttributesDefinitions?.map((tagAttributesDefinition) => {
        const tag = { attributes: [] as Attribute[] } as Tag;

        tag.name = tagAttributesDefinition.view;
        tag.attributes =
          tagAttributesDefinition.editableAttributes?.map((attributeName) => {
            const attribute = {} as Attribute;
            const attributeValue = getAttributeInAttributes(element, attributeName, tagAttributesDefinition.model);

            attribute.name = attributeName;
            attribute.value = attributeValue;
            attribute.parentName = tagAttributesDefinition.model;

            return attribute;
          }) ?? [];

        return tag;
      }) ?? []
    );
  }, [node, tagAttributesDefinitions, isOpen]);

  /**
   * 属性ダイアログの「適用」ボタンのクリックイベントハンドラ。
   * @param event イベント。
   * @param tagIndex タグインデックス。
   */
  const handleEditAttributeApplyButtonClick = () => {
    if (!node) {
      console.error('対象ノードが指定されていません。');
      return;
    }

    if (node.is('$text')) {
      console.error('現在はテキストノードの属性値設定は実装されていません。');
      return;
    }

    const element = node as Element;

    editor.model.change((writer: Writer) => {
      tags.forEach((tag) => {
        tag.attributes.forEach((attribute) => {
          const attributeValue = attribute.value as string | number | boolean | string[];

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let attributes = element.getAttribute(attribute.parentName) as any;
          if (!attributes) {
            attributes = {};
          }

          const copiedAttributes = JSON.parse(JSON.stringify(attributes));
          if (!copiedAttributes.attributes) {
            copiedAttributes.attributes = {} as Record<string, string | number | boolean>;
          }
          if (attributeValue === '') {
            delete copiedAttributes.attributes[attribute.name];
          } else {
            copiedAttributes.attributes[attribute.name] = attributeValue;
          }

          // PSかどうかによってconfirmed属性を調整する
          if ('confirmed' in copiedAttributes.attributes) {
            if (
              'ps_no' in copiedAttributes.attributes ||
              'ps_derivation_id' in copiedAttributes.attributes ||
              'ps_version_id' in copiedAttributes.attributes
            ) {
              const isNotPs =
                copiedAttributes.attributes.ps_no === '' ||
                copiedAttributes.attributes.ps_derivation_id === '' ||
                copiedAttributes.attributes.ps_version_id === '' ||
                copiedAttributes.attributes.ps_no === '@@@' ||
                copiedAttributes.attributes.ps_derivation_id === '@@@' ||
                copiedAttributes.attributes.ps_version_id === '@@@' ||
                !copiedAttributes.attributes.ps_no ||
                !copiedAttributes.attributes.ps_derivation_id ||
                !copiedAttributes.attributes.ps_version_id;

              // PSではない場合、title属性は削除
              if (isNotPs && copiedAttributes.attributes.title) {
                delete copiedAttributes.attributes.title;
              }

              switch (copiedAttributes.attributes.confirmed) {
                case '0':
                case '2':
                  copiedAttributes.attributes.confirmed = isNotPs ? '2' : '0';
                  break;
                case '1':
                case '3':
                  copiedAttributes.attributes.confirmed = isNotPs ? '3' : '1';
                  break;
                default:
                  break;
              }
            }
          }

          writer.setAttribute(
            attribute.parentName,
            copiedAttributes as Record<string, string | number | boolean>,
            element
          );
        });
      });
    });

    setIsOpen(false);
  };

  const handleEditAttributeCancelButtonClick = () => {
    setIsOpen(false);
  };

  const handleOnChangeAttributeValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    tagIndex: number,
    attributeIndex: number
  ) => {
    tags[tagIndex].attributes[attributeIndex].value = event.target.value;
    setTags(tags);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      className="attribute-dialog"
      overlayClassName="attribute-dialog-overlay"
      shouldCloseOnOverlayClick={false}
      ariaHideApp={false}
    >
      {tags &&
        tags.map((tag, tagIndex) => (
          <div key={uuidv4()}>
            <div className="header">
              <strong>{tag.name}</strong>
              {t(' - Attributes')}
            </div>
            <table>
              <tbody>
                {tag.attributes.map((attribute, attributeIndex) => (
                  <tr key={uuidv4()} className="attribute">
                    <td className="name">{attribute.name}</td>
                    <td className="value">
                      <input
                        type="text"
                        defaultValue={tag.attributes[attributeIndex].value}
                        onChange={(e) => handleOnChangeAttributeValue(e, tagIndex, attributeIndex)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      <div className="buttons">
        <input type="button" value="キャンセル" className="btnbg-g" onClick={handleEditAttributeCancelButtonClick} />
        <input type="button" value="適用" onClick={handleEditAttributeApplyButtonClick} disabled={editor.isReadOnly} />
      </div>
    </ReactModal>
  );
}

export { AttributeDialog };
